/**
 * Spec 027 — conversaciones medidas que han fallado, por negocio.
 * Plegado: resumen. Clic: lista numerada del último lote.
 */
import { useQuery } from '@tanstack/react-query'
import { Link } from 'react-router'

import { api } from '../lib/api'

type Motivo = 'codigo' | 'juez' | 'ambos'

interface Falla {
    labId: number
    runId: string
    personaId: string
    conversationId: number | null
    motivo: Motivo
    nota: number | null
    razon: string | null
    cuando: string
}

interface Grupo {
    tenantId: string
    accountId: number | null
    nombre: string
    fallos: Falla[]
    resumen: { total: number; codigo: number; juez: number; texto: string }
}

interface Lote {
    id: string
    nombre: string
    conversaciones: number
}

interface Respuesta {
    grupos: Grupo[]
    laboratorio: 'ok' | 'sin_cablear' | 'caido'
    publicUrl: string | null
    lote: Lote | null
}

const ETIQUETA: Record<Motivo, string> = {
    codigo: 'código',
    juez: 'juez',
    ambos: 'código y juez',
}

function hrefConversacion(g: Grupo, f: Falla, publicUrl: string | null): string | null {
    if (g.accountId !== null && f.conversationId !== null) {
        return `/business/${g.accountId}/console/${f.conversationId}`
    }
    if (publicUrl) return `${publicUrl.replace(/\/+$/, '')}/conversaciones/${f.labId}`
    return null
}

export function FallosLaboratorio() {
    const { data, isLoading } = useQuery<Respuesta>({
        queryKey: ['fallos-laboratorio'],
        queryFn: () => api.get<Respuesta>('/overview/fallos-laboratorio'),
    })

    if (isLoading || !data) return null

    if (data.laboratorio !== 'ok') {
        return (
            <section className="space-y-2" data-testid="fallos-laboratorio">
                <h3 className="text-xs font-medium uppercase tracking-wide text-rose-400/80">
                    Conversaciones que han ido mal
                </h3>
                <p className="text-xs text-neutral-500">
                    {data.laboratorio === 'sin_cablear'
                        ? 'El laboratorio no está cableado en este panel.'
                        : 'El laboratorio no contesta. El apartado no inventa filas.'}
                </p>
            </section>
        )
    }

    const total = data.grupos.reduce((n, g) => n + g.fallos.length, 0)
    const lote = data.lote
    const pieLote = lote
        ? `${lote.nombre}${lote.conversaciones > 0 ? ` · ${total} de ${lote.conversaciones}` : ''}`
        : null

    if (total === 0) {
        return (
            <section className="space-y-2" data-testid="fallos-laboratorio">
                <h3 className="text-xs font-medium uppercase tracking-wide text-neutral-500">
                    Conversaciones que han ido mal
                </h3>
                <p className="text-xs text-neutral-500">
                    {lote
                        ? `Ninguna del último lote (${lote.nombre}) ha fallado por código ni por el juez.`
                        : 'Ninguna medida reciente ha fallado por código ni por el juez.'}
                </p>
            </section>
        )
    }

    return (
        <section className="space-y-3" data-testid="fallos-laboratorio">
            <h3 className="text-xs font-medium uppercase tracking-wide text-rose-400/80">
                Conversaciones que han ido mal
                <span className="ml-2 font-normal normal-case tracking-normal text-neutral-500">
                    último lote{pieLote ? ` · ${pieLote}` : ''}
                </span>
            </h3>

            <div className="space-y-2">
                {data.grupos.map((g) => (
                    <details
                        key={g.tenantId}
                        className="group rounded-lg border border-rose-500/20 bg-neutral-900/40"
                    >
                        <summary className="flex cursor-pointer list-none items-baseline justify-between gap-3 px-3 py-2 [&::-webkit-details-marker]:hidden">
                            <span className="flex min-w-0 items-baseline gap-2">
                                <span
                                    aria-hidden
                                    className="text-[10px] text-neutral-600 transition-transform group-open:rotate-90"
                                >
                                    ▶
                                </span>
                                {g.accountId !== null ? (
                                    <Link
                                        to={`/business/${g.accountId}`}
                                        className="text-sm font-medium text-neutral-100 hover:text-sky-400"
                                        onClick={(e) => e.stopPropagation()}
                                    >
                                        {g.nombre}
                                    </Link>
                                ) : (
                                    <span className="text-sm font-medium text-neutral-100">{g.nombre}</span>
                                )}
                            </span>
                            <span className="shrink-0 text-xs tabular-nums text-neutral-500">{g.resumen.texto}</span>
                        </summary>
                        <ol className="divide-y divide-neutral-900 border-t border-neutral-800">
                            {g.fallos.map((f, i) => {
                                const href = hrefConversacion(g, f, data.publicUrl)
                                return (
                                    <li
                                        key={f.labId}
                                        className="flex flex-wrap items-baseline justify-between gap-2 px-3 py-2"
                                    >
                                        <div className="flex min-w-0 gap-2">
                                            <span className="w-5 shrink-0 text-xs tabular-nums text-neutral-600">
                                                {i + 1}.
                                            </span>
                                            <div className="min-w-0">
                                                {href && href.startsWith('http') ? (
                                                    <a href={href} className="text-sm text-sky-400 hover:text-sky-300">
                                                        {f.personaId}
                                                    </a>
                                                ) : href ? (
                                                    <Link to={href} className="text-sm text-sky-400 hover:text-sky-300">
                                                        {f.personaId}
                                                    </Link>
                                                ) : (
                                                    <span className="text-sm text-neutral-200">{f.personaId}</span>
                                                )}
                                                <p className="mt-0.5 text-[11px] text-neutral-500">
                                                    {ETIQUETA[f.motivo]}
                                                    {f.nota !== null ? ` · ${f.nota.toFixed(2)}` : ''}
                                                    {f.razon ? ` · ${f.razon}` : ''}
                                                </p>
                                            </div>
                                        </div>
                                        {g.accountId !== null && (
                                            <Link
                                                to={`/business/${g.accountId}/calendar`}
                                                className="text-[11px] text-neutral-500 hover:text-sky-400"
                                            >
                                                Calendario
                                            </Link>
                                        )}
                                    </li>
                                )
                            })}
                        </ol>
                    </details>
                ))}
            </div>
        </section>
    )
}
